// ═══════════════════════════════════════════════════════════════════════════
// Permisos por rol — Etapa 3
//
// El operador: "solo el super usuario de la vinícola puede dejar en blanco
// todo, los vendedores u operarios solo en su ámbito de acción."
//
// ⚠ ESTO ES INTERFAZ, NO SEGURIDAD. Ocultar un botón no impide nada: quien
//   abra las herramientas del navegador puede invocar la función igual. El
//   control REAL vive en Postgres (RLS, migración 0002) y llega con la
//   Etapa 4, cuando los datos dejen de estar en el dispositivo.
//
//   Lo que esta tabla aporta hoy: que la interfaz no ofrezca lo que no
//   corresponde, y que la estructura esté escrita y probada ANTES de que
//   existan vendedores reales. Un permiso pensado con un solo usuario es más
//   barato que uno improvisado con cinco.
//
// Los cuatro roles vienen del modelo (`app_role`): owner, manager, accountant,
// agent. El super-admin de PLATAFORMA es otra cosa y vive aparte
// (MODELO-DATOS §1.1): administra vinícolas, no opera dentro de ellas.
// ═══════════════════════════════════════════════════════════════════════════

export const PERMISOS = {
  // ── Destructivo: solo el dueño ──
  borrar_todo:            ["owner"],
  importar_datos:         ["owner", "manager"],

  // ── La pantalla de Config entera ──
  // Fuente de datos, llaves de Facturapi, token de sync, folio, respaldos y
  // zona de peligro. Nada de eso es del vendedor: son decisiones de la casa, y
  // enseñárselas solo produce preguntas que no debería tener que hacerse.
  ver_configuracion:      ["owner", "manager"],

  // ── Credenciales: tocan dinero y el SAT ──
  config_facturapi:       ["owner"],
  config_sync:            ["owner"],
  datos_vinicola:         ["owner", "manager"],   // incluye cuentas bancarias

  // ── Catálogo: define lo que todos venden ──
  editar_productos:       ["owner", "manager"],
  editar_precios:         ["owner", "manager"],

  // La PESTAÑA de Productos. El operador: "No puede editar los productos,
  // entonces es inoperante la pestaña de productos, debe desaparecer. Solo
  // puede seleccionarlos cuando selecciona un cliente y genera una remisión."
  //
  // No pierde nada: al levantar la remisión ve el nombre, el precio que le
  // toca a ESE cliente y las existencias de cada producto. La pestaña solo
  // repetía eso sin dejarle tocar nada.
  ver_catalogo:           ["owner", "manager", "accountant"],

  // El vendedor VE existencias —necesita saber si puede prometer entrega—
  // pero sin costos: cuánto hay es operación, cuánto costó es de la casa.
  ver_existencias:        ["owner", "manager", "accountant", "agent"],

  // Mover mercancía entre bodegas es administrar, no vender. El vendedor no
  // traslada: su remisión ya descuenta sola, y darle esto sería darle con qué
  // cuadrar a mano lo que vendió. La regla de verdad vive en la base
  // (`trasladar_inventario` exige `es_gerencia`); esto es para que la pantalla
  // no ofrezca lo que el servidor va a negar.
  administrar_bodegas:    ["owner", "manager", "accountant"],

  // ── El precio NO lo mueve el vendedor; lo PIDE ──
  // Decisión del operador, primero como negativa —"No puede mover el descuento,
  // puede sugerirlo. Hasta ahora ha sido una llamada o un mensaje"— y después
  // con el camino completo: "Puede solicitar precio especial; pero debe
  // autorizarlo un administrador o el dueño."
  //
  // Además del argumento comercial hay uno aritmético: la comisión es 10% del
  // total, así que quien controla el precio controla su propia base. Por eso
  // pedir y autorizar NUNCA pueden ser la misma persona en la misma solicitud
  // — se comprueba en la base, no aquí (migración 0026).
  editar_descuento:       ["owner", "manager"],
  solicitar_precio:       ["owner", "manager", "agent"],
  autorizar_precio:       ["owner", "manager"],

  // ── Cartera ──
  // "no queremos que canibalice a la vinícola que tiene clientes establecidos
  //  o a otros vendedores". Quién atiende a quién lo decide la casa.
  asignar_cartera:        ["owner", "manager"],

  // ── Operación diaria ──
  crear_remision:         ["owner", "manager", "agent"],
  alta_cliente:           ["owner", "manager", "agent"],
  ver_todos_los_clientes: ["owner", "manager", "accountant"],  // el agente ve su cartera

  // ── Corregir una remisión: cancelar y rehacer, no editar ────────────────
  //
  // El operador describió el flujo real y sacó la conclusión él mismo:
  //
  //   "Levanta un pedido, llega a bodega, lo surte y entrega y se marca como
  //    entregado. Si cometió un error solo puede cancelar la remisión y hacer
  //    una nueva. Es más limpio."
  //
  // Y tiene razón en que es más limpio: editar un documento que ya salió a
  // bodega deja dos verdades —lo que se surtió y lo que dice el papel— sin
  // nada que diga cuál es cuál. Cancelar y rehacer deja las dos, numeradas y
  // con motivo.
  //
  // ESTO INVIERTE la regla anterior, que era del mismo operador: el vendedor
  // no cancelaba, por el escenario de emitir, cobrar en efectivo y hacer
  // desaparecer el documento. Lo que cambió no es el criterio sino el terreno:
  // hoy cancelar NO borra —queda el motivo, quién y cuándo, el folio se
  // consume y la remisión sigue en la lista— así que el fraude que aquello
  // cerraba ya lo cierra el diseño. Se decidió a sabiendas, con la regla vieja
  // sobre la mesa.
  //
  // Y solo las de SU cartera: la RLS no le deja ver otras (migración 0024),
  // así que no puede cancelar lo que no puede leer.
  //
  // ── Y desde 0064, también CORREGIR, pero no el vendedor ──
  // El operador: "Debería ser posible editar las remisiones, es algo sujeto a
  // errores. Internamente puede quedar en el log lo que cambió." Cancelar y
  // rehacer seguía siendo el camino, pero el botón Editar existía y su aviso
  // se ignoraba: al cliente le llegó una remisión con error. La objeción de
  // arriba —dos verdades sin nada que diga cuál es cuál— la resuelve la
  // bitácora: `editar_remision` guarda el antes y el después. La regla de
  // verdad vive en la base, que exige dueño o gerencia.
  editar_remision:        ["owner", "manager"],
  cancelar_remision:      ["owner", "manager", "agent"],

  // El folio lo asigna el servidor. Teclear otro no es "elegir número": si el
  // folio ya existe, la remisión de ese folio se REEMPLAZA — era la puerta de
  // atrás para editar sin permiso de editar. El operador: "no puede usar un
  // folio distinto".
  usar_folio_manual:      ["owner", "manager"],

  // ── Fiscal y cobranza ──
  timbrar_cfdi:           ["owner", "manager", "accountant"],
  cancelar_cfdi:          ["owner", "accountant"],
  registrar_pago:         ["owner", "manager", "accountant"],

  // ── Lectura ──
  // `ver_estadisticas` gobierna también Historial y Cuentas: son las dos
  // pantallas que cruzan clientes para administrar, no para vender.
  ver_estadisticas:       ["owner", "manager", "accountant"],

  // La lista del vendedor, con los tres estados juntos. Gerencia también la
  // tiene: sirve para ver lo mismo que ve él cuando reporta algo.
  ver_mis_ventas:         ["owner", "manager", "agent"],

  // ── Lo del vendedor, que es suyo ──
  // Sus comisiones y su CLABE. Nadie más ve lo de otro: el trato de cada quien
  // no es información de la vinícola que se comparta entre el equipo.
  ver_mis_comisiones:     ["owner", "manager", "accountant", "agent"],
  editar_mi_perfil:       ["owner", "manager", "accountant", "agent"],
};

/** ¿Este rol puede hacer esta acción? Sin rol conocido, no. */
export function puede(rol, accion) {
  const permitidos = PERMISOS[accion];
  if (!permitidos) return false;          // acción desconocida → denegar
  return permitidos.includes(rol);
}

/** Versión cómoda a partir de la membresía. */
export const puedeMembresia = (membresia, accion) => puede(membresia?.role, accion);

/** Explicación para la interfaz cuando algo está bloqueado. */
export function motivoBloqueo(rol, accion) {
  const permitidos = PERMISOS[accion] || [];
  const nombres = { owner: "el propietario", manager: "un gerente",
                    accountant: "contabilidad", agent: "un vendedor" };
  const quienes = permitidos.map(r => nombres[r] || r);
  const lista = quienes.length > 1
    ? quienes.slice(0, -1).join(", ") + " o " + quienes.at(-1)
    : quienes[0] || "nadie";
  return `Esta acción solo puede realizarla ${lista}.`;
}
